/**
 * Admin Password Reset Requests - Approve/Reject password resets
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import { ArrowLeft, Lock, Check, X, RefreshCw, Mail, Phone, Clock, User, Eye, Send, AlertCircle } from "lucide-react";

const AdminPasswordResets = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [processing, setProcessing] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);

  const fetchRequests = useCallback(async () => {
    try {
      const response = await api.get("/password-reset/requests" + (filter !== "all" ? "?status=" + filter : ""));
      setRequests(response.data?.requests || []);
    } catch (error) {
      toast.error("Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = async (requestId, customPassword = null) => {
    setProcessing(requestId);
    try {
      const response = await api.post("/password-reset/approve", {
        request_id: requestId,
        approve: true,
        new_password: customPassword
      });
      
      if (response.data.email_sent) {
        toast.success("Password reset approved and email sent!");
      } else {
        toast.success("Password reset approved. New password: " + response.data.new_password);
      }
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (requestId, notes = null) => {
    setProcessing(requestId);
    try {
      await api.post("/password-reset/approve", {
        request_id: requestId,
        approve: false,
        admin_notes: notes || "Request rejected by admin"
      });
      toast.success("Request rejected");
      fetchRequests();
    } catch (error) {
      toast.error("Failed to reject");
    } finally {
      setProcessing(null);
    }
  };

  const handlePreviewEmail = async (userId) => {
    try {
      const response = await api.post("/password-reset/send-credentials", {
        user_id: userId,
        preview: true
      });
      setPreviewModal(response.data);
    } catch (error) {
      toast.error("Failed to preview email");
    }
  };

  const handleSendCredentials = async (userId) => {
    setProcessing(userId);
    try {
      const response = await api.post("/password-reset/send-credentials", {
        user_id: userId,
        preview: false
      });
      
      if (response.data.email_sent) {
        toast.success("Credentials sent to " + response.data.recipient);
      } else {
        toast.warning("Email failed. Temp password: " + response.data.temp_password);
      }
      setPreviewModal(null);
    } catch (error) {
      toast.error("Failed to send credentials");
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950" data-testid="admin-password-resets">
      <header className="bg-slate-900 border-b border-slate-800 px-4 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/admin")} className="p-2 text-slate-400 hover:text-white">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Lock className="h-5 w-5 text-amber-400" />
                Password Reset Requests
              </h1>
              <p className="text-sm text-slate-400">Approve or reject password resets</p>
            </div>
          </div>
          <Button variant="ghost" onClick={fetchRequests} className="p-2 text-slate-400">
            <RefreshCw className="h-5 w-5" />
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Filter Tabs */}
        <div className="flex gap-2">
          {["pending", "approved", "rejected", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={"px-4 py-2 rounded-lg text-sm font-medium transition-colors " + (filter === f ? "bg-emerald-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700")}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Requests List */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          {requests.length === 0 ? (
            <div className="p-8 text-center">
              <Lock className="h-12 w-12 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-400">No {filter !== "all" ? filter : ""} requests</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {requests.map((req) => (
                <div key={req.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-slate-800 rounded-lg">
                          <User className="h-5 w-5 text-slate-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{req.user_name}</p>
                          <div className="flex items-center gap-3 text-sm text-slate-400">
                            {req.user_email && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {req.user_email}
                              </span>
                            )}
                            {req.user_phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {req.user_phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {req.reason && (
                        <p className="text-sm text-slate-500 mb-2 pl-12">Reason: {req.reason}</p>
                      )}
                      
                      <div className="flex items-center gap-4 text-xs text-slate-500 pl-12">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(req.created_at).toLocaleString()}
                        </span>
                        <span className={"px-2 py-0.5 rounded-full " + (req.status === "pending" ? "bg-amber-500/20 text-amber-400" : req.status === "approved" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400")}>
                          {req.status}
                        </span>
                      </div>
                    </div>
                    
                    {req.status === "pending" && (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handlePreviewEmail(req.user_id)}
                          className="text-slate-400 hover:text-white"
                          data-testid={"preview-" + req.id}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(req.id)}
                          disabled={processing === req.id}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={"approve-" + req.id}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleReject(req.id)}
                          disabled={processing === req.id}
                          data-testid={"reject-" + req.id}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Email Preview Modal */}
      {previewModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Email Preview</h3>
              <Button variant="ghost" onClick={() => setPreviewModal(null)} className="p-2">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
              <div>
                <span className="text-sm text-slate-400">To:</span>
                <p className="text-white">{previewModal.recipient}</p>
              </div>
              <div>
                <span className="text-sm text-slate-400">Subject:</span>
                <p className="text-white">{previewModal.subject}</p>
              </div>
              <div>
                <span className="text-sm text-slate-400">Password:</span>
                <p className="text-emerald-400 font-mono text-lg">{previewModal.temp_password || previewModal.new_password}</p>
              </div>
              <div className="border border-slate-700 rounded-lg overflow-hidden">
                <div dangerouslySetInnerHTML={{ __html: previewModal.html_content }} />
              </div>
            </div>
            <div className="p-4 border-t border-slate-800 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setPreviewModal(null)}>Cancel</Button>
              <Button onClick={() => handleSendCredentials(previewModal.user_id || requests.find(r => r.user_email === previewModal.recipient)?.user_id)} className="bg-emerald-600 hover:bg-emerald-700">
                <Send className="h-4 w-4 mr-2" />
                Send Email
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPasswordResets;
